import { useState } from "react";
import { trpc } from "../../trpc";
import { StepMerchant } from "./wizard/StepMerchant";
import { StepAgentRouting } from "./wizard/StepAgentRouting";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Stepper, useSteps } from "@/components/ui/stepper";
import { toast } from "sonner";

type RoutingWizardData = {
  merchant: {
    id: string;
    displayName: string;
    whatsappAccessToken: string;
    whatsappPhoneNumberId: string;
    billingCycleDay?: number;
    minimumSubscriptionUsage?: number;
    unitPriceBelowMinimum?: number;
    unitPriceAboveMinimum?: number;
  } | null;
  agent: {
    tools: ("transferConversation" | "assignAgent")[];
    systemPrompt: string;
  } | null;
};

type RoutingAgentWizardProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const steps = [
  { title: "Merchant", description: "Seleccionar o crear merchant" },
  { title: "Agente Enrutador", description: "Configurar agente enrutador" },
];

const RoutingAgentWizard = ({ isOpen, onClose, onSuccess }: RoutingAgentWizardProps) => {
  const { activeStep, goToNext, goToPrevious } = useSteps({ count: steps.length });
  const [wizardData, setWizardData] = useState<RoutingWizardData>({
    merchant: null,
    agent: null,
  });

  const createMerchantMutation = trpc.agent.createMerchant.useMutation();
  const createAgentMutation = trpc.agent.createAgent.useMutation();

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      goToNext();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      goToPrevious();
    }
  };

  const handleReset = () => {
    goToPrevious();
    goToPrevious();
    setWizardData({
      merchant: null,
      agent: null,
    });
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const updateWizardData = <K extends keyof RoutingWizardData>(
    step: K,
    data: RoutingWizardData[K]
  ) => {
    setWizardData((prev) => ({
      ...prev,
      [step]: data,
    }));
  };

  const handleMerchantSubmit = async (merchantData: any, createNew: boolean) => {
    try {
      if (createNew) {
        const result = await createMerchantMutation.mutateAsync(merchantData);
        updateWizardData("merchant", {
          id: result.id,
          displayName: merchantData.displayName,
          whatsappAccessToken: merchantData.whatsappAccessToken,
          whatsappPhoneNumberId: merchantData.whatsappPhoneNumberId,
          billingCycleDay: merchantData.billingCycleDay,
          minimumSubscriptionUsage: merchantData.minimumSubscriptionUsage,
          unitPriceBelowMinimum: merchantData.unitPriceBelowMinimum,
          unitPriceAboveMinimum: merchantData.unitPriceAboveMinimum,
        });
      } else {
        updateWizardData("merchant", merchantData);
      }
      handleNext();
    } catch (error: any) {
      toast.error(error.message || "Error creating merchant");
    }
  };

  const handleAgentSubmit = async (agentData: any) => {
    try {
      if (!wizardData.merchant) {
        throw new Error("Missing merchant data");
      }

      await createAgentMutation.mutateAsync({
        merchantId: wizardData.merchant.id,
        tools: agentData.tools,
        systemPrompt: agentData.systemPrompt,
        fallback: true,
      });

      toast.success("Agente enrutador creado exitosamente");

      handleClose();
      onSuccess?.();
    } catch (error: any) {
      toast.error(error.message || "Error creating routing agent");
    }
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <StepMerchant
            onSubmit={handleMerchantSubmit}
            initialData={wizardData.merchant}
            isLoading={createMerchantMutation.isLoading}
          />
        );
      case 1:
        return <StepAgentRouting onSubmit={handleAgentSubmit} initialData={wizardData.agent} />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle>Crear Agente Enrutador</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-8">
          <Stepper activeStep={activeStep} steps={steps.map((s) => ({ title: s.title }))} />

          <div className="min-h-[400px]">{renderStep()}</div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={handleBack} disabled={activeStep === 0}>
              Atrás
            </Button>
            <Button variant="ghost" onClick={handleClose}>
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RoutingAgentWizard;
